import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { Portal, TableCell } from '@mui/material';
import FlexBox from 'components/common/FlexBox';
import IconButton from 'components/common/buttons/IconButton';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import TextTableCell from 'components/common/table/TextTableCell';
import { addItemsModalOpenVar, highlightedItemIdVar } from '../../variables';
import { useReactiveVar } from '@apollo/client';
import ResidualValueGridAddItemsComponent from './ResidualValueGridAddItemsComponent';
import sortBy from 'lodash.sortby';
import { ResidualValueGridHeaderRow, ResidualValueGridTableRow, SelectAllCheckbox, SelectCheckbox } from '../../Styles';
import { onCheckBoxChange, stopPropagation } from 'utils/propertyChangeAdapters';
import WrappedFakeText from 'components/common/WrappedFakeText';
import MaxHeightTable from 'components/common/MaxHeightTable';
import { valueOrDefault } from 'utils/functions';
import { Typography } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';

const defaultActionsSlotId = 'default-item-actions-slot';

const ResidualValueGridItemsComponent = ({ lens, loading, actionsSlotId, disabled, pending }) => {
  const { t } = useTranslation();

  const highlightedItemId = useReactiveVar(highlightedItemIdVar);
  const addItemsModalOpen = useReactiveVar(addItemsModalOpenVar);

  const existingItemIds = ((lens |> get) || []).map(x => x.id);

  const addItems = items => {
    const existingItems = lens |> get;
    const newItems = [...existingItems, ...items];
    (lens |> set)(sortBy(newItems, [item => item.name.toLowerCase()]));
    addItemsModalOpenVar(false);
  };

  const deleteSelectedItems = () => {
    const items = lens |> get;

    const selectionHasHighlight = items.some(item => item.selected && item.id === highlightedItemId);
    if (selectionHasHighlight) highlightedItemIdVar(null);

    (lens |> set)(items.filter(item => !item.selected));
  };

  const select = useCallback(
    id => {
      if (pending) return;
      highlightedItemIdVar(id);
      const detailsCard = document.getElementById('items-bottom-anchor');
      detailsCard?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
    [pending]
  );

  const selectAll = () => {
    const items = lens |> get;
    let selected = true;

    if (items.every(item => item.selected)) selected = false;

    (lens |> set)(items.map(item => ({ ...item, selected })));
  };

  const [noneSelected, allSelected, noItems] = (function () {
    const items = lens |> get;
    return [items.every(item => !item.selected), items.every(item => item.selected), items.length === 0];
  })();

  return (
    <FlexBox direction='column' gap={1}>
      {!actionsSlotId && <FlexBox gap={0.5} justifyContent='end' id={defaultActionsSlotId} />}
      <MaxHeightTable maxHeight={600}>
        <MaxHeightTable.Head>
          <ResidualValueGridHeaderRow>
            <ThemedHeaderTableCell width={100}>
              <SelectAllCheckbox checked={allSelected && !noItems} onClick={selectAll} disabled={disabled || noItems} />
            </ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('ResidualValueGrid.ItemName')}</ThemedHeaderTableCell>
          </ResidualValueGridHeaderRow>
        </MaxHeightTable.Head>
        <MaxHeightTable.Body>
          {loading && (
            <ResidualValueGridTableRow>
              <TableCell colSpan={2}>
                <WrappedFakeText lines={6} />
              </TableCell>
            </ResidualValueGridTableRow>
          )}
          {!loading && noItems && (
            <ResidualValueGridTableRow>
              <TableCell colSpan={5} align='center'>
                <Typography>{t('TableIsEmpty')}</Typography>
              </TableCell>
            </ResidualValueGridTableRow>
          )}
          {!loading &&
            !noItems &&
            (lens |> sequence).map(itemLens => (
              <ResidualValueGridTableRow
                hover
                selectable
                selected={highlightedItemId === (itemLens.id |> get)}
                key={itemLens.id |> get}
                onClick={() => select(itemLens.id |> get)}
              >
                <TableCell width={100}>
                  <SelectCheckbox
                    checked={itemLens.selected |> get |> valueOrDefault(false)}
                    onClick={stopPropagation()}
                    onChange={itemLens.selected |> set |> onCheckBoxChange}
                    disabled={disabled}
                  />
                </TableCell>
                <TextTableCell content={itemLens.name |> get} />
              </ResidualValueGridTableRow>
            ))}
        </MaxHeightTable.Body>
      </MaxHeightTable>
      {addItemsModalOpen && <ResidualValueGridAddItemsComponent existingItemIds={existingItemIds} onAdd={addItems} />}
      <Portal container={() => document.getElementById(actionsSlotId || defaultActionsSlotId)}>
        <IconButton type='add' onClick={() => addItemsModalOpenVar(true)} disabled={disabled || loading} />
        <IconButton type='delete' color='error' onClick={deleteSelectedItems} disabled={disabled || noneSelected || loading} />
      </Portal>
      <FlexBox position='absolute' bottom={-200} id='items-bottom-anchor' />
    </FlexBox>
  );
};

ResidualValueGridItemsComponent.propTypes = {
  lens: PropTypes.object,
  loading: PropTypes.bool,
  actionsSlotId: PropTypes.string,
  disabled: PropTypes.bool,
  pending: PropTypes.bool
};

export default ResidualValueGridItemsComponent;
